import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Building2, Clock, ChevronRight, Briefcase, Loader2 } from "lucide-react";
import { useMyApplications } from "@/hooks/useJobApplications";
import { format } from "date-fns";
import { getStatusLabel, getStatusColor } from "@/lib/applicationStatus";

export default function AlunoCandidaturas() {
  const navigate = useNavigate();
  const { applications, isLoading } = useMyApplications();

  if (isLoading) {
    return <div className="page-container flex items-center justify-center min-h-[50vh]"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  const candidaturas = applications.map(app => {
    const job = app.job_posts as any;
    const org = job?.partner_orgs;
    return {
      id: app.id,
      vagaId: app.job_id,
      titulo: job?.title ?? "Vaga",
      empresa: org?.name ?? "",
      tipo: job?.employment_type ?? "",
      status: app.status,
      dataAplicacao: format(new Date(app.created_at), "dd/MM/yyyy"),
    };
  });

  const activeStatuses = ["aplicada", "triagem", "entrevista_rh", "entrevista_gestor", "proposta"];

  return (
    <div className="page-container">
      <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate("/aluno/vagas")}><ArrowLeft className="w-5 h-5" /></Button>
        <div>
          <h1 className="font-heading text-xl font-bold text-foreground">Minhas Candidaturas</h1>
          <p className="text-sm text-muted-foreground">Acompanhe o status das suas aplicações</p>
        </div>
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="grid grid-cols-3 gap-3 mb-6">
        <div className="bg-card rounded-xl p-3 border border-border text-center">
          <p className="text-2xl font-bold text-foreground">{candidaturas.length}</p>
          <p className="text-[10px] text-muted-foreground">Total</p>
        </div>
        <div className="bg-card rounded-xl p-3 border border-border text-center">
          <p className="text-2xl font-bold text-primary">{candidaturas.filter(c => activeStatuses.includes(c.status)).length}</p>
          <p className="text-[10px] text-muted-foreground">Em andamento</p>
        </div>
        <div className="bg-card rounded-xl p-3 border border-border text-center">
          <p className="text-2xl font-bold text-success">{candidaturas.filter(c => c.status === "contratado").length}</p>
          <p className="text-[10px] text-muted-foreground">Contratado</p>
        </div>
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="space-y-3">
        {candidaturas.map((candidatura) => (
          <div key={candidatura.id} onClick={() => navigate(`/aluno/vagas/candidaturas/${candidatura.id}`)} className="card-interactive cursor-pointer">
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                <Building2 className="w-6 h-6 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <h3 className="font-medium text-foreground line-clamp-1">{candidatura.titulo}</h3>
                  <ChevronRight className="w-5 h-5 text-muted-foreground shrink-0" />
                </div>
                <p className="text-sm text-muted-foreground mb-2">{candidatura.empresa}</p>
                <div className="flex items-center justify-between">
                  <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(candidatura.status)}`}>
                    {getStatusLabel(candidatura.status)}
                  </span>
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <Clock className="w-3 h-3" />{candidatura.dataAplicacao}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </motion.div>

      {candidaturas.length === 0 && (
        <div className="bg-muted/50 rounded-2xl p-8 text-center">
          <Briefcase className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="font-heading font-semibold text-foreground mb-2">Nenhuma candidatura ainda</h3>
          <p className="text-sm text-muted-foreground mb-4">Explore as vagas disponíveis e comece a se candidatar</p>
          <Button variant="accent" onClick={() => navigate("/aluno/vagas")}>Ver Vagas</Button>
        </div>
      )}
    </div>
  );
}
